import { useDispatch, useSelector } from 'react-redux'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { FlexColumn, FlexRow } from '../../components/atoms/layout/Flex'
import Label from '../../components/atoms/display/Label'
import Button from '../../components/atoms/inputs/Button'
import PdfViewer from '../../components/atoms/display/PdfViewer'
import usePdf from '../../hooks/usePdf'
import { AgreementSelectors } from '../../store/agreement/selectors'
import { AgreementActions } from '../../store/agreement'
import { SessionSelectors } from '../../store/session/selectors'
import useDay from '../../hooks/useDay'
import { Agreement, AgreementFlow, AgreementStatusAccept, AgreementStatusReject } from '../../types/agreement'
import Link from '../../components/atoms/navigation/Link'
import { CompanySelectors } from '../../store/company/selectors'
import { useAgreementAcceptDialog, useAgreementRejectDialog } from '../../hooks/useDialog'
import AvatarUserStepper from '../../components/molecules/display/AvatarUserStepper'
import useAgreements from '../../hooks/useAgreements'
import { Company } from '../../types/Company'
import { User } from '../../types/User'

function status(agreement?: Agreement, companyId?: string) {
  if (agreement === undefined || companyId === undefined) {
    return ''
  }
  if (agreement.status === AgreementStatusAccept) {
    return '締結済み'
  }
  if (agreement.status === AgreementStatusReject) {
    return '却下済み'
  }
  const companyState = agreement.companies.find((agreementCompany) => agreementCompany.company.id === companyId)
  if (companyState?.confirmedAt == null) {
    return '自社確認中'
  }
  return '取引先確認中'
}

function displayAcceptButtons(
  agreement?: Agreement,
  company?: Company,
  user?: User,
  currentFlow?: AgreementFlow | null
) {
  if (agreement == null || company == null || user == null) {
    return false
  }

  if (currentFlow != null) {
    return currentFlow.user.id === user.id
  }

  const companyState = agreement?.companies.find((agreementCompany) => agreementCompany.company.id === company.id)

  return companyState?.confirmedAt == null && company?.representative?.userId === user?.id
}

export default function AgreementDetailPage() {
  const { findFlows, findCurrentFlow } = useAgreements()
  const agreement = useSelector(AgreementSelectors.agreement)
  const flows = agreement ? findFlows(agreement) ?? [] : []
  const currentFlow = agreement ? findCurrentFlow(agreement) : null
  const companyId = useSelector(SessionSelectors.companyId)
  const user = useSelector(SessionSelectors.user)
  const company = useSelector(CompanySelectors.company)
  const { formatDateTime } = useDay()
  const dispatch = useDispatch()
  const { agreementId } = useParams()
  const pdfParams = usePdf({ filename: agreement?.fileName })
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { Download } = pdfParams.downloadPlugin
  const companyState = agreement?.companies.find((agreementCompany) => agreementCompany.company.id === companyId)
  const clientState = agreement?.companies.find((agreementCompany) => agreementCompany.company.id !== companyId)
  const acceptDialog = useAgreementAcceptDialog()
  const rejectDialog = useAgreementRejectDialog()

  useEffect(() => {
    if (agreementId) {
      dispatch(AgreementActions.fetchAgreement({ agreementId }))
    }
  }, [agreementId, dispatch])

  return (
    <FlexColumn>
      <Link href="/agreements">契約管理に戻る</Link>
      <FlexRow>
        <FlexRow width={128}>
          <Label text="企業名" />
        </FlexRow>
        <Label text={clientState?.company.name ?? ''} />
      </FlexRow>
      <FlexRow>
        <FlexRow width={128}>
          <Label text="契約書名" />
        </FlexRow>
        <Label text={agreement?.name ?? ''} />
      </FlexRow>
      <FlexRow>
        <FlexRow width={128}>
          <Label text="自社確認日時" />
        </FlexRow>
        {companyState?.confirmedAt ? (
          <Label text={formatDateTime(companyState.confirmedAt)} />
        ) : (
          <Label text="未確認" />
        )}
      </FlexRow>
      <FlexRow>
        <FlexRow width={128}>
          <Label text="取引先確認日時" />
        </FlexRow>
        {clientState?.confirmedAt ? <Label text={formatDateTime(clientState.confirmedAt)} /> : <Label text="未確認" />}
      </FlexRow>
      <FlexRow>
        <FlexRow width={128}>
          <Label text="承認フロー" />
        </FlexRow>
        {agreement && currentFlow && (
          <AvatarUserStepper
            users={flows.map((flow) => flow.user) ?? []}
            activeStep={flows.map((flow) => flow.user.id).indexOf(currentFlow.user.id)}
          />
        )}
      </FlexRow>
      <FlexRow align="center">
        <FlexRow width={128}>
          <Label text="ステータス" />
        </FlexRow>
        <Label text={status(agreement, companyId)} />
        {agreement && displayAcceptButtons(agreement, company, user, currentFlow) && (
          <FlexRow>
            <Button height="small" onClick={() => acceptDialog.open(agreement.id)}>
              承認する
            </Button>
            <Button height="small" color="caution" onClick={() => rejectDialog.open(agreement.id)}>
              却下する
            </Button>
          </FlexRow>
        )}
      </FlexRow>
      {agreement?.rejectReason && (
        <FlexRow>
          <FlexRow width={128}>
            <Label text="却下理由" />
          </FlexRow>
          <Label text={agreement.rejectReason} whiteSpace="pre" />
        </FlexRow>
      )}
      <Download>
        {({ onClick }) => (
          <Button height="small" onClick={onClick}>
            契約書ダウンロード
          </Button>
        )}
      </Download>
      <FlexRow justify="center">
        <FlexColumn width="80%">
          {agreement && <PdfViewer url={agreement.fileUrl} httpHeaders={{}} params={pdfParams} />}
        </FlexColumn>
      </FlexRow>
    </FlexColumn>
  )
}
